import {
  FormControlLabel,
  Button,
  Grid,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {Controller} from 'react-hook-form';
import {AurSelect, Option} from '../../common/aur-select/AurSelect';
import {
  AddClientInfoInputKey,
  ProjectDetails,
  Workspace,
} from '../../pages/user-management/models';
import {t} from 'i18next';
import AurSelectDropdown from '../../common/aur-select-dropdown/AurSelectDropdown';
import {ISelectItem} from '../../common/aur-select-dropdown/types';
import {useEffect, useState} from 'react';
import AurWorkspaceChipList from '../../user-management/aur-workspace-chip/AurWorkspaceChipList';
import AurPermissionGroupSelect from '../../common/aur-select/AurPermissionGroupSelect';
import AurClientSelect from '../../common/aur-select-dropdown/AurClientSingleSelect';
import AurCheckbox from '../../common/aur-checkbox/AurCheckbox';
import {UserRoleIds} from '../../../types/user-management/userManagementTypes';
import AurProjectSelect from '../../common/aur-select/AurProjectSelect';
import {ClientProject} from '../../../types/project-type/projectDetailsType';
import {getWorkspacesWithDisplayValue} from '../../pages/user-management/tabs/utils';
import {PERMISSION_GROUP_CATEGORY} from '../../../constants/constants';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {getFilteredRoleOptionList} from '../../../utils/userManagementUtils';
import {AurClientProjectSectionHeader} from '../../common/aur-page-header/AurClientProjectHeader';

interface ItemType {
  ItemKey: string;
  ItemName: string;
  ItemDescription: string;
  ItemFeatures: string[];
  ItemFeaturesForECI: string[];
  ItemFeaturesForBudget: string[];
  ItemEvidenceSourceManagement: string[];
  ItemFeaturesForSystem: string[];
  ItemFeaturesForManageProject: string[];
  ItemFeaturesForBusinessIntelligence: string[];
}

interface AurClientFormFieldsProps {
  control: any;
  roleOptionList: Option[];
  permissionOptionList: ItemType[];
  projectPermissionOptionList: ItemType[];
  projects: ClientProject[];
  HCprojects: ClientProject[];
  unsavedProjects: ProjectDetails[];
  workspaceOptionList: ISelectItem[];
  isValid: boolean;
  buttonLabel: string;
  buttonVisible: boolean;
  clientInfoData?: {
    clientId?: string;
    clientName?: string;
    role?: string;
    project?: string;
    workspaces?: ISelectItem[];
    permissionGroup?: string;
    permissionGroupId?: string;
  };
  renderEcaPermissionView?: React.ReactNode;
  renderBudgetEstimatedView?: React.ReactNode;
  onProjectChange: (value: string) => void;
  onClientChange: (value: Option) => void;
  onRoleChange: (value: string) => void;
  onPermissionChange: (value: string) => void;
  onHCProjectCheckedChange: (value: boolean) => void;
  setClientLoading?: (value: boolean) => void;
  onCreateNewPermissionGroup?: (
    clientId: string,
    category: PERMISSION_GROUP_CATEGORY
  ) => void;

  // new props
  hideProjectAddSection?: boolean;
  hasHCProjects?: boolean;
  showHCProjects?: boolean;
}

const AurClientFormFields = (props: AurClientFormFieldsProps) => {
  const {
    control,
    roleOptionList,
    permissionOptionList,
    projectPermissionOptionList,
    projects,
    HCprojects,
    unsavedProjects,
    workspaceOptionList = [],
    isValid,
    buttonLabel,
    buttonVisible,
    clientInfoData,
    renderEcaPermissionView,
    renderBudgetEstimatedView,
    onProjectChange,
    onClientChange,
    onRoleChange,
    onPermissionChange,
    onHCProjectCheckedChange,
    // new props
    hideProjectAddSection = false,
    hasHCProjects = false,
    showHCProjects = false,
    setClientLoading,
    onCreateNewPermissionGroup,
  } = props;

  const selectedUserRoleForClient = useSelector(
    (state: RootState) => state?.userRole?.userRoleForSelectedClient
  );

  const [selectedWorkspaces, setSelectedWorkspaces] = useState<
    ISelectItem[] | undefined
  >(
    getWorkspacesWithDisplayValue(
      clientInfoData?.workspaces as unknown as Workspace[]
    ) as unknown as ISelectItem[]
  );

  const [clientId, setClientId] = useState<string | undefined>(
    clientInfoData?.clientId
  );

  const [role, setRole] = useState<string | undefined>(clientInfoData?.role);
  const [projectPermissionGroup, setProjectPermissionGroup] = useState<
    string | undefined
  >('');
  const [clientPermissionGroup, setClientPermissionGroup] = useState<
    string | undefined
  >(clientInfoData?.permissionGroupId);
  const [project, setProject] = useState<string | undefined>(
    clientInfoData?.project
  );

  const [, setPermissionGroupDisabled] = useState(true);

  useEffect(() => {
    if (!clientId || !role) setPermissionGroupDisabled(true);
    else if (
      role === UserRoleIds.SystemAdmin ||
      role === UserRoleIds.ClientAdmin
    ) {
      setPermissionGroupDisabled(false);
    } else setPermissionGroupDisabled(false);
  }, [clientId, role]);

  useEffect(() => {
    if (clientInfoData) {
      setRole(clientInfoData?.role);
    }
  }, [clientInfoData]);

  useEffect(() => {
    if (hideProjectAddSection) {
      setSelectedWorkspaces([]);
      setProject('');
    }
  }, [hideProjectAddSection]);

  useEffect(() => {
    if (!unsavedProjects?.length) {
      setProjectPermissionGroup('');
    }
  }, [unsavedProjects]);

  const getHCCheckbox = () => {
    return hasHCProjects ? (
      <FormControlLabel
        control={<AurCheckbox />}
        checked={showHCProjects}
        className="hc-project-grant-access-checkbox"
        onChange={(event: React.SyntheticEvent, checked: boolean) => {
          onHCProjectCheckedChange && onHCProjectCheckedChange(checked);
        }}
        label={
          <Typography className="mist-color-500">
            {t('addEditTeamMember.form.highlyConfidentialProjectNote')}
          </Typography>
        }
      />
    ) : (
      <></>
    );
  };

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    onProjectChange(event.target.value);
  };

  const isAdministrator = role === UserRoleIds.ClientAdmin;

  return (
    <>
      <Grid container className="add-client-info-container" spacing={3}>
        <AurClientProjectSectionHeader
          title={t('addEditTeamMember.clientAccess')}
          subheader={t('addEditTeamMember.clientAccessSubheader')}
          style={{paddingTop: '0', marginLeft: 28}}
        />

        <Grid item sm={8} md={12} className="form-content">
          <Controller
            name={AddClientInfoInputKey.client}
            control={control}
            render={({field, fieldState}) => (
              <AurClientSelect
                field={{
                  ...field,
                  value: clientId,
                  label: clientInfoData?.clientName,
                  onChange: (option) => {
                    setClientId(option.value);
                    field.onChange({target: {value: option.value}});
                    onClientChange(option);
                    setProject('');
                    setSelectedWorkspaces([]);
                  },
                }}
                fieldState={fieldState}
                setInitialLoading={setClientLoading}
                isAdminOnlyView={true}
                isUserViewOnlyScreen={true}
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={12} className="form-content">
          <Grid
            container
            spacing={3}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
            }}
          >
            <Grid item sm={8} md={12} className="form-content">
              <Controller
                name={AddClientInfoInputKey.role}
                control={control}
                render={({field, fieldState}) => (
                  <AurSelect
                    dataTestId="aur-test-role-dropdown"
                    label={t('addEditTeamMember.form.role')}
                    optionList={getFilteredRoleOptionList(
                      selectedUserRoleForClient ?? '',
                      roleOptionList
                    )}
                    disabled={!clientId}
                    field={{
                      ...field,
                      value: role,
                      onChange: (event: SelectChangeEvent<string>) => {
                        const newRole = event.target.value;
                        if (
                          role === UserRoleIds.ClientAdmin ||
                          newRole === UserRoleIds.ClientAdmin
                        ) {
                          setProject('');
                          setSelectedWorkspaces([]);
                          setProjectPermissionGroup('');
                        }
                        setRole(newRole);
                        field.onChange(event);
                        onRoleChange(newRole);
                      },
                    }}
                    fieldState={fieldState}
                  />
                )}
              />
            </Grid>

            {!isAdministrator && (
              <Grid item sm={8} md={12} className="form-content">
                <Controller
                  name={AddClientInfoInputKey.permissionGroup}
                  control={control}
                  render={({field}) => (
                    <AurPermissionGroupSelect
                      disabled={!(clientId && role)}
                      items={permissionOptionList ?? []}
                      value={clientPermissionGroup || ''}
                      onChange={(value) => {
                        setClientPermissionGroup(value);
                        field.onChange(value);
                        onPermissionChange(value);
                      }}
                      label={t('addEditTeamMember.form.clientPermissionGroup')}
                      clientId={clientId}
                      onCreateNewPermissionGroup={(clientId) =>
                        onCreateNewPermissionGroup &&
                        onCreateNewPermissionGroup(
                          clientId,
                          PERMISSION_GROUP_CATEGORY.CLIENT
                        )
                      }
                      subheaderLabel={t('common.clientPermGroupSubheader')}
                      disabledMessage={t(
                        'addEditTeamMember.form.clientPermissionGroupDisabledMessage'
                      )}
                      category={PERMISSION_GROUP_CATEGORY.CLIENT}
                    />
                  )}
                />
              </Grid>
            )}

            {isAdministrator && (
              <Grid item sm={8} md={12} className="form-content">
                <Typography className="client-info-hidden-note-text">
                  {' '}
                  {t('addEditTeamMember.form.sysAdminContentAccess')}
                  <span>
                    {t('addEditTeamMember.form.projectAndWorkspaces')}
                  </span>{' '}
                  {t('addEditTeamMember.form.withClientinAurora')}
                </Typography>
                {getHCCheckbox()}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      {!hideProjectAddSection && !isAdministrator && (
        <Grid
          container
          className="add-client-info-container"
          sx={{marginTop: '24px'}}
          spacing={3}
        >
          <AurClientProjectSectionHeader
            title={t('addEditTeamMember.projectAccess')}
            subheader={t('addEditTeamMember.projectAccessSubheader')}
            style={{paddingTop: '0', marginLeft: 28, marginTop: 48}}
          />
          <Grid item sm={8} md={12} className="form-content">
            <Controller
              name={AddClientInfoInputKey.projects}
              control={control}
              render={({field, fieldState}) => (
                <AurProjectSelect
                  field={{
                    ...field,
                    value: project,
                    onChange: (event) => {
                      handleProjectChange(event);
                      setProject(event.target.value);
                      setSelectedWorkspaces([]);
                      field.onChange(event);
                    },
                  }}
                  fieldState={fieldState}
                  disabled={!(clientId && role && clientPermissionGroup)}
                  showHCProjects={showHCProjects}
                  isAdmin={isAdministrator}
                  clientProjects={projects}
                  clientHCProjects={HCprojects}
                  unsavedProjects={unsavedProjects}
                />
              )}
            />
          </Grid>
          {!isAdministrator && (
            <Grid item sm={8} md={12} className="form-content">
              <Controller
                name={AddClientInfoInputKey.projPermissionGroup}
                control={control}
                render={({field}) => (
                  <AurPermissionGroupSelect
                    disabled={!project}
                    items={projectPermissionOptionList ?? []}
                    value={projectPermissionGroup || ''}
                    onChange={(value) => {
                      setProjectPermissionGroup(value);
                      field.onChange(value);
                    }}
                    subheaderLabel={t('common.projectPermGroupSubheader')}
                    label={t('addEditTeamMember.form.permissionGroup')}
                    clientId={clientId}
                    onCreateNewPermissionGroup={(clientId) =>
                      onCreateNewPermissionGroup &&
                      onCreateNewPermissionGroup(
                        clientId,
                        PERMISSION_GROUP_CATEGORY.PROJECT
                      )
                    }
                    disabledMessage={t(
                      'addEditTeamMember.form.permissionGroupDefaultMessage'
                    )}
                    category={PERMISSION_GROUP_CATEGORY.PROJECT}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item sm={8} md={12} className="form-content">
            <Controller
              name={AddClientInfoInputKey.workspaces}
              control={control}
              render={({field, fieldState}) => (
                <AurSelectDropdown
                  label={t('addEditTeamMember.form.workspaces')}
                  items={workspaceOptionList}
                  defaultSelection={selectedWorkspaces}
                  field={field}
                  fieldState={fieldState}
                  itemKey="workspaceId"
                  itemDisplay="workspaceDisplay"
                  dropdownLabel={t('addEditTeamMember.form.workspaces')}
                  search={false}
                  selectAll={false}
                  submitButtonText={t('common.select')}
                  labelId={'project-group'}
                  disabled={!project}
                  onSelectionChange={(selectedItems) => {
                    setSelectedWorkspaces(selectedItems);
                  }}
                  onClearClicked={() => {
                    setSelectedWorkspaces([]);
                  }}
                  blurOnFocusOut={true}
                  dataTestId="aur-test-workspaces-dropdown"
                  helperText={
                    !project
                      ? t('addEditTeamMember.form.workspacesHelperText')
                      : ''
                  }
                  helperTextClass="helptext-color"
                />
              )}
            />
            <AurWorkspaceChipList
              items={selectedWorkspaces || []}
              itemKey="workspaceId"
              itemLabel="workspaceDisplay"
              onDelete={(deletedItem, remainingItems) => {
                setSelectedWorkspaces(remainingItems);
              }}
            />
          </Grid>
        </Grid>
      )}

      {showHCProjects && isAdministrator && !hideProjectAddSection && (
        <>
          <Grid container className="add-client-info-container" spacing={3}>
            <AurClientProjectSectionHeader
              title={t('addEditTeamMember.projectAccess')}
              subheader={t('addEditTeamMember.projectAccessSubheader')}
              className="project-access-header"
            />
            <Grid item sm={8} md={12} className="form-content">
              <Controller
                name={AddClientInfoInputKey.projects}
                control={control}
                render={({field, fieldState}) => (
                  <AurProjectSelect
                    field={{
                      ...field,
                      value: project,
                      onChange: (event) => {
                        handleProjectChange(event);
                        setProject(event.target.value);
                        setSelectedWorkspaces([]);
                        field.onChange(event);
                      },
                    }}
                    fieldState={fieldState}
                    disabled={!clientId}
                    showHCProjects={showHCProjects}
                    isAdmin={isAdministrator}
                    clientProjects={projects}
                    clientHCProjects={HCprojects}
                    unsavedProjects={[]}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className="add-client-info-container" spacing={3}>
            {buttonVisible && (
              <Grid item sm={9} md={12} className="text-right user-form">
                <Button
                  className="button-continue typo-text buttonLarge_size submit-btn"
                  type="submit"
                  disabled={!project}
                >
                  {buttonLabel}
                </Button>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {!isAdministrator && renderEcaPermissionView && (
        <Grid container className="add-client-info-container" spacing={3}>
          <Grid item sm={8} md={12} className="form-content eci-section">
            {renderEcaPermissionView}
          </Grid>
        </Grid>
      )}
      {!isAdministrator && renderBudgetEstimatedView && (
        <Grid container className="add-client-info-container" spacing={3}>
          <Grid
            item
            sm={8}
            md={12}
            className="form-content budget-estimated-section"
          >
            {renderBudgetEstimatedView}
          </Grid>
        </Grid>
      )}
      {!hideProjectAddSection && !isAdministrator ? (
        <Grid container className="add-client-info-container" spacing={3}>
          {buttonVisible && (
            <Grid item sm={8} md={12} className="text-right user-form">
              <Button
                className="button-continue typo-text buttonLarge_size submit-btn"
                type="submit"
                disabled={!isValid || !projectPermissionGroup}
              >
                {buttonLabel}
              </Button>
            </Grid>
          )}
        </Grid>
      ) : (
        ''
      )}
    </>
  );
};

export default AurClientFormFields;
